import React from "react";
import MainLayout from "../layout/MainLayout";
import { motion } from "framer-motion";
import { Car, Calendar, User, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Drone1Img from "../assets/drone1.jpg";

export const Landing = () => {
  return (
    <MainLayout>
      {/* Hero Section */}
      <section
        className="relative h-[80vh] rounded-3xl mx-4 sm:mx-10 my-6 overflow-hidden shadow-xl bg-cover bg-center bg-no-repeat flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${Drone1Img})` }}
      >
        <div className="absolute inset-0 bg-black/70 z-0" />
        <div className="relative z-10 max-w-3xl text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl sm:text-5xl font-bold mb-4 just-me-again-down-here-regular"
          >
            Eco-Friendly Car Hire & Travel Services in Uganda
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg sm:text-xl mb-6 text-white/90"
          >
            Eco-friendly tours, premium car hire, and reliable logistics for
            travellers, NGOs, and institutions across Uganda and East Africa.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Link
              to="/contact"
              className="px-6 py-3 bg-teal-600 hover:bg-teal-700 rounded-lg font-semibold transition"
            >
              Book a Car
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 bg-white/20 border border-white/30 hover:bg-white/30 rounded-lg font-semibold transition"
            >
              Request a Quote
            </Link>
            <Link
              to="/drone"
              className="px-6 py-3 bg-white/20 border border-white/30 hover:bg-white/30 rounded-lg font-semibold transition"
            >
              Learn About Our Drone
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-7xl mx-auto md:flex md:flex-row md:items-center text-center md:text-start px-4 py-8 md:py-12 mb-20">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-xl md:text-5xl font-bold mb-4 just-me-again-down-here-regular"
        >
          Reliable Car Hire Across Uganda – Trusted by NGOs, Businesses &
          Travellers
        </motion.h2>
        <p className="text-gray-700 dark:text-gray-300 text-lg">
          Muhaad is your trusted travel and logistics partner across Uganda and
          beyond, offering eco-friendly rides, expert drivers, and flexible
          bookings. Whether you're planning a safari, managing NGO fieldwork, or
          just need a smooth, reliable trip, we deliver comfort, safety, and
          professionalism every time
        </p>
      </section>

      {/* Highlights */}
      <section className="max-w-8xl mx-auto px-4 grid gap-8 sm:grid-cols-2 lg:grid-cols-5 mb-24">
        {[
          {
            icon: <User className="w-8 h-8 text-teal-500" />,
            title: "With or Without a Driver",
            desc: "Prefer privacy or need a guide? We offer both self-drive and chauffeur options.",
            link: "/with-or-without-driver",
          },
          {
            icon: <MapPin className="w-8 h-8 text-teal-500" />,
            title: "Drop-off & Pick-up Services",
            desc: "We’ll bring the car to you and pick it up when you're done. Convenient and fast.",
            link: "/drop-off-and-pickup-services",
          },
          {
            icon: <Calendar className="w-8 h-8 text-teal-500" />,
            title: "Flexible Scheduling",
            desc: "No strict schedules. Modify your plan easily with our flexible booking system.",
            link: "/contact",
          },
          {
            icon: <Car className="w-8 h-8 text-teal-500" />,
            title: "Premium Comfort",
            desc: "Air-conditioned, well-maintained, and ready for Ugandan roads.",
            link: "/premium-comfort"
          },
          {
            icon: <Car className="w-8 h-8 text-teal-500" />,
            title: "Eco-Conscious Drone Transport",
            desc: (
              <>
                For light transport and delivery solutions.
                <Link
                  to="/drone"
                  className="text-teal-600 hover:underline ml-1"
                >
                  Learn more →
                </Link>
              </>
            ),
          },
        ].map((item, index) => {
          const Wrapper = item.link ? Link : "div";
          const wrapperProps = item.link ? { to: item.link } : {};

          return (
            <Wrapper
              key={index}
              {...wrapperProps}
              className="p-6 rounded-xl backdrop-blur-md bg-white/30 dark:bg-white/10 border border-white/20 dark:border-white/10 shadow-lg text-center hover:scale-[1.02] transition"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="mb-4 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {item.desc}
                </p>
              </motion.div>
            </Wrapper>
          );
        })}
      </section>

      {/* CTA Banner */}
      <section className="text-center px-4 pb-20">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-4xl font-semibold mb-4 just-me-again-down-here-regular"
        >
          Ready to get moving?
        </motion.h3>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          Muhaad is here to help with fast bookings, flexible terms, and 24/7
          support.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition"
        >
          Book Now
        </Link>
      </section>
    </MainLayout>
  );
};
